import { API_BASE } from '../config/apiBase';
import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = API_BASE;

/**
 * Internal: tracks active session time and flushes minutes to a sender callback.
 * - Pauses while the tab is hidden (document.hidden === true).
 * - Sends accumulated whole minutes every 2 minutes, on visibility-hide, and on unmount.
 * - Idempotent: each minute is sent exactly once. No double-counting on unmount.
 */
function useTimeAccumulator(isActive, sender) {
  // Total accumulated *active* milliseconds, only updated when we know how much elapsed.
  const accumulatedMsRef = useRef(0);
  // Timestamp when the current visible/active segment started. Null if currently paused.
  const segmentStartRef = useRef(null);
  // Total whole minutes already flushed to backend.
  const sentMinutesRef = useRef(0);

  const closeSegment = () => {
    if (segmentStartRef.current != null) {
      accumulatedMsRef.current += Date.now() - segmentStartRef.current;
      segmentStartRef.current = null;
    }
  };

  const openSegment = () => {
    if (segmentStartRef.current == null) {
      segmentStartRef.current = Date.now();
    }
  };

  const flush = useCallback(() => {
    // Compute total elapsed including any currently-open segment without closing it.
    const live = segmentStartRef.current != null
      ? Date.now() - segmentStartRef.current
      : 0;
    const totalMinutes = Math.floor((accumulatedMsRef.current + live) / 60000);
    const unsent = totalMinutes - sentMinutesRef.current;
    if (unsent > 0) {
      sentMinutesRef.current = totalMinutes;
      sender(unsent);
    }
  }, [sender]);

  useEffect(() => {
    if (!isActive) return;
    
    // Start tracking only when the tab is visible
    if (!document.hidden) openSegment();
    
    const intervalId = setInterval(() => {
      if (!document.hidden) flush();
    }, 60000); // check every minute
    
    const onVisibilityChange = () => {
      if (document.hidden) {
        closeSegment();
        flush();
      } else {
        openSegment();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    
    const onBeforeUnload = () => {
      closeSegment();
      const totalMinutes = Math.floor(accumulatedMsRef.current / 60000);
      const unsent = totalMinutes - sentMinutesRef.current;
      if (unsent > 0) {
        sentMinutesRef.current = totalMinutes;
        // sendBeacon for reliable delivery on page close
        sender(unsent, true);
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Final flush on unmount (single source of truth — never double counts)
      closeSegment();
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);
}

/**
 * Hook to track time spent on a specific lesson.
 * Posts minutes to /training/lessons/{lessonId}/progress.
 */
export function useTimeTracking(lessonId, isActive = true) {
  const sender = useCallback((minutes, useBeacon = false) => {
    if (!lessonId || minutes <= 0) return;
    const url = `${API}/training/lessons/${lessonId}/progress`;
    const body = { completed: false, time_spent_minutes: Math.round(minutes) };
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: 'application/json' }));
    } else {
      axios.post(url, body).catch(() => {});
    }
  }, [lessonId]);
  
  useTimeAccumulator(isActive && !!lessonId, sender);
  return {};
}

/**
 * Hook to track time spent on quiz / final-exam / sandbox / other non-lesson pages.
 * Posts minutes to /training/time-spent which increments total_time_spent_minutes
 * without touching any specific lesson record.
 */
export function useGenericTimeTracking(context, contextId = null, isActive = true) {
  const sender = useCallback((minutes, useBeacon = false) => {
    if (minutes <= 0) return;
    const url = `${API}/training/time-spent`;
    const body = { minutes: Math.round(minutes), context, context_id: contextId };
    if (useBeacon && navigator.sendBeacon) {
      const token = localStorage.getItem('token');
      // sendBeacon can't set Authorization headers; backend accepts unauthenticated beacon
      // gracefully (will simply 401 and silently fail). For best-effort delivery on unload.
      navigator.sendBeacon(url, new Blob([JSON.stringify({ ...body, _t: token })], { type: 'application/json' }));
    } else {
      axios.post(url, body).catch(() => {});
    }
  }, [context, contextId]);
  
  useTimeAccumulator(isActive, sender);
}

export default useTimeTracking;
